import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, RefreshCw, Search, X } from 'lucide-react';
import { apiClient } from '../api';
import orgApi from '../api/organizations';
import { useAuth } from '../auth/AuthProvider';
import { useAssistantRecord } from '../contexts/ChatContext';
import { errMessage, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../components/UI/ink';
import NewCaseDrawer from '../components/Cases/NewCaseDrawer';
import {
  KC_SHORT, NEXT, PRIORITY_LABEL, PRIORITY_TOKEN, STATUS_LABEL, SYSTEM, OUTCOME_LABEL,
  age, ago, caseRef, furthestStage, initials, isOverdue, kcSentence, stamp,
} from '../components/Cases/model';

// Every case, who owns it, and how far the attack got. The status strip
// filters; a row opens the docked preview (?case=); "Open case" goes to the
// full page.
const LIMIT = 25;
const selectStyle = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };
const STRIP = [
  ['open', 'OPEN', 'not started'],
  ['investigating', 'INVESTIGATING', 'being worked'],
  ['remediation', 'REMEDIATION', 'fixing'],
  ['closed', 'CLOSED', 'last 30 days'],
];

export const PriorityChip = ({ p, big }) => {
  const t = PRIORITY_TOKEN[p] || 'low';
  return (
    <span className={`inline-flex items-center gap-1.5 ${big ? 'h-6 px-[9px] text-[11px]' : 'h-[22px] px-2 text-[10.5px]'} rounded-md font-mono font-medium tracking-[0.06em] whitespace-nowrap`} style={{ background: `var(--sev-${t}-bg)`, color: 'var(--ink-text)' }}>
      <span className="w-1.5 h-1.5 rounded-[1px]" style={{ background: `var(--sev-${t}-fg)` }} />{PRIORITY_LABEL[p] || String(p).toUpperCase()}
    </span>
  );
};

export const KillChainBar = ({ kc = [], big }) => {
  const last = furthestStage(kc);
  return (
    <span className="flex gap-0.5" aria-label={last < 0 ? 'No kill-chain evidence' : `Kill chain reached ${KC_SHORT[last]}`}>
      {KC_SHORT.map((s, i) => (
        <span key={s} title={`${s}${kc[i] ? ` · ${kc[i]}` : ''}`} className={`flex-grow ${big ? 'h-1.5' : 'h-2'} rounded-sm`} style={{ background: kc[i] ? (i === last ? 'var(--sev-crit-fg)' : 'var(--sev-high-fg)') : 'var(--surf-line)' }} />
      ))}
    </span>
  );
};

export default function Cases() {
  const { user } = useAuth();
  const me = user?.email || user?.sub || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('active');
  const [view, setView] = useState('all');
  const [priority, setPriority] = useState('');
  const [sort, setSort] = useState('priority');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [members, setMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);

  const selectedId = searchParams.get('case');
  const select = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('case', id); else next.delete('case');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [status, view, priority, sort, debounced]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ page: String(page), limit: String(LIMIT), sort });
      if (status) p.set('status', status);
      if (view !== 'all') p.set('view', view);
      if (priority) p.set('priority', priority);
      if (debounced) p.set('search', debounced);
      const r = await apiClient.get(`/api/cases?${p}`);
      setData(r.data);
    } catch (err) {
      setError(errMessage(err, 'Could not load cases.'));
    } finally {
      setLoading(false);
    }
  }, [page, sort, status, view, priority, debounced]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { orgApi.getMembers().then(m => setMembers((m?.members || m?.users || []).filter(x => x.email && (!x.status || x.status === 'active')))).catch(() => {}); }, []);

  useEffect(() => {
    if (!selectedId) { setPreview(null); return; }
    setPreview(p => (p?.id === selectedId ? p : null));
    apiClient.get(`/api/cases/${selectedId}`).then(r => setPreview(r.data?.case || null)).catch(() => setPreview(false));
  }, [selectedId]);
  useAssistantRecord(preview ? { kind: 'case', id: preview.id, label: preview.title } : null);

  const nameOf = useCallback((email) => members.find(m => m.email?.toLowerCase() === String(email || '').toLowerCase())?.full_name || email, [members]);
  const cases = data?.cases || [];
  const counts = data?.status_counts || {};
  const activeTotal = (counts.open || 0) + (counts.investigating || 0) + (counts.remediation || 0);
  const quick = data?.quick_counts || {};

  const takeIt = async (id) => {
    try {
      await apiClient.post(`/api/cases/${id}/assign`, { assigned_to: me });
      load();
      apiClient.get(`/api/cases/${id}`).then(r => setPreview(r.data?.case || null)).catch(() => {});
    } catch (err) {
      setError(errMessage(err, 'Could not assign the case to you.'));
    }
  };

  const summary = !data ? 'Loading…' : [
    `${activeTotal} open`,
    `${data.critical_open} critical`,
    `${quick.unassigned} unassigned`,
    data.oldest_open_at ? `oldest open ${age(data.oldest_open_at)}` : null,
  ].filter(Boolean).join(' · ');

  const evidence = useMemo(() => {
    if (!preview) return [];
    return [
      ...(preview.findings || []).map(f => ({ key: f.finding_id, kind: 'FINDING', sev: f.severity, t: f.title, m: [f.finding_id, f.server_hostname, f.status].filter(Boolean).join(' · '), to: `/findings?finding=${f.finding_id}` })),
      ...(preview.alerts || []).map(a => ({
        key: `${a.alert_type}-${a.alert_id}`, kind: a.alert_type.toUpperCase(), sev: a.severity,
        t: a.alert_type === 'ioc' ? `${a.ioc_type || ''} ${a.ioc_value || `alert ${a.alert_id}`}` : `${a.cve_id || `alert ${a.alert_id}`}${a.package_name ? ` · ${a.package_name}` : ''}`,
        m: [`${a.alert_type.toUpperCase()} alert ${a.alert_id}`, a.server_hostname].filter(Boolean).join(' · '), to: `/security-alerts?alert=${a.alert_type}-${a.alert_id}`,
      })),
    ];
  }, [preview]);
  const sevTok = (s) => ({ critical: 'crit', high: 'high', medium: 'med', low: 'low' }[String(s || '').toLowerCase()] || 'low');

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-4" style={{ color: 'var(--ink-text)' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>INTELLIGENCE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Cases</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" onClick={load} disabled={loading} aria-label="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border disabled:opacity-60" style={secondaryBtnStyle}><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} /></button>
          <button type="button" onClick={() => setCreating(true)} className={primaryBtn} style={primaryBtnStyle}><Plus className="w-4 h-4" strokeWidth={2} />New case</button>
        </div>
      </div>

      {error && <div role="alert" className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={{ background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' }}><span>{error}</span><button type="button" onClick={() => setError(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button></div>}

      {/* Status strip — doubles as the filter */}
      <div role="group" aria-label="Case status" className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
        {STRIP.map(([id, k, sub], i) => {
          const on = status === id;
          const n = id === 'closed' ? data?.closed_last_30_days : counts[id];
          return (
            <button key={id} type="button" onClick={() => setStatus(on ? 'active' : id)} aria-pressed={on} className="h-[70px] box-border flex flex-col justify-center gap-1 px-4 rounded-xl border text-left" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
              <span className="flex items-center justify-between"><span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{k}</span>{i < STRIP.length - 1 && <ChevronRight className="w-3.5 h-3.5 hidden md:block" strokeWidth={2} style={{ color: 'var(--surf-line-strong)' }} />}</span>
              <span className="flex items-baseline gap-2"><span className="font-heading text-2xl font-semibold leading-none">{data ? n || 0 : '—'}</span><span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{sub}</span></span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-[0_1_280px] min-w-[180px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-[15px] h-[15px] shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
          <input type="search" aria-label="Search cases" placeholder="Title, case ID, host or IoC" value={q} onChange={e => setQ(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        {[['mine', 'Mine'], ['unassigned', 'Unassigned'], ['auto', 'Auto-created']].map(([id, t]) => {
          const on = view === id;
          return (
            <button key={id} type="button" onClick={() => setView(on ? 'all' : id)} aria-pressed={on} className="h-10 flex items-center gap-1.5 px-3 rounded-[10px] border text-[13px]" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
              {t}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{quick[id] ?? '—'}</span>
            </button>
          );
        })}
        <div className="flex flex-wrap gap-2 md:ml-auto">
          <label htmlFor="f-pri" className="sr-only">Priority</label>
          <select id="f-pri" value={priority} onChange={e => setPriority(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
            <option value="">Any priority</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          <label htmlFor="f-sort" className="sr-only">Sort</label>
          <select id="f-sort" value={sort} onChange={e => setSort(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
            <option value="priority">Priority, then latest activity</option><option value="updated">Recently updated</option><option value="oldest">Oldest first</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-5 items-start">
        <section aria-label="Cases" className="rounded-[14px] border overflow-hidden min-w-0" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <div className="h-[42px] box-border flex items-center px-4 border-b" style={{ borderColor: 'var(--surf-line)' }}>
            <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>{data ? `${data.total} ${status === 'active' ? 'ACTIVE' : STATUS_LABEL[status]?.toUpperCase() || ''} CASE${data.total === 1 ? '' : 'S'}` : 'CASES'}</span>
            <span className="ml-auto font-mono text-[10px] tracking-[0.1em] hidden sm:inline" style={{ color: 'var(--ink-faint)' }}>KILL CHAIN</span>
          </div>
          {!cases.length && (
            <div className="py-12 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
              {loading ? 'Loading cases…' : debounced || view !== 'all' || priority ? 'No cases match these filters.' : status === 'active' ? 'No open cases. High and critical alerts open one automatically, or start one with New case.' : 'No cases here.'}
            </div>
          )}
          {cases.map(c => {
            const on = c.id === selectedId;
            const hosts = (c.hosts || []).map(h => h.hostname);
            const ev = (c.finding_count || 0) + (c.alert_count || 0);
            return (
              <div key={c.id} className="relative">
                <button type="button" onClick={() => select(on ? null : c.id)} aria-pressed={on} className="w-full min-h-[76px] box-border flex items-center gap-3.5 px-4 py-2.5 border-b text-left" style={{ borderColor: 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'transparent' }}>
                  <span className="w-[82px] shrink-0"><PriorityChip p={c.priority} /></span>
                  <span className="flex-grow min-w-0 flex flex-col gap-[5px]">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">{c.title}</span>
                      {c.auto_created && <span className="h-[18px] shrink-0 inline-flex items-center px-[5px] rounded-[5px] border font-mono text-[9.5px] tracking-[0.06em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>AUTO</span>}
                      {isOverdue(c) && <span className="h-[18px] shrink-0 inline-flex items-center px-[5px] rounded-[5px] font-mono text-[9.5px] tracking-[0.06em]" style={{ background: 'var(--sev-high-bg)' }} title="Open longer than its priority's target">OVERDUE</span>}
                    </span>
                    <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>
                      {[caseRef(c.id), c.status === 'closed' && c.resolution_outcome ? `Closed · ${OUTCOME_LABEL[c.resolution_outcome]}` : STATUS_LABEL[c.status], `${ev} evidence`, hosts.length ? hosts.slice(0, 2).join(', ') + (hosts.length > 2 ? ` +${hosts.length - 2}` : '') : null].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span title={c.assigned_to ? nameOf(c.assigned_to) : 'Unassigned'} className="w-[30px] h-[30px] shrink-0 box-border rounded-full flex items-center justify-center text-[11px] font-semibold" style={c.assigned_to ? { background: 'var(--surf-raised)', border: '1px solid var(--surf-line-strong)' } : { border: '1px dashed var(--surf-line-strong)', color: 'var(--ink-faint)' }}>
                    {c.assigned_to ? initials(nameOf(c.assigned_to), c.assigned_to) : '?'}
                  </span>
                  <span className="w-[84px] shrink-0 hidden sm:block"><KillChainBar kc={c.kill_chain} /></span>
                  <span className="w-[50px] shrink-0 text-right font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{ago(c.updated_at)}</span>
                </button>
                {on && <span aria-hidden="true" className="absolute right-0 top-[26px] w-px h-6" style={{ background: 'var(--accent)' }} />}
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-xs" style={{ color: 'var(--ink-faint)' }}>
            <span className="flex-grow">Kill chain: Recon · Weaponise · Deliver · Exploit · Install · C2 · Act. Filled stages have ATT&amp;CK-tagged evidence in the case.</span>
            {data && data.total_pages > 1 && (
              <span className="flex items-center gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page" className="w-8 h-8 flex items-center justify-center rounded-lg border disabled:opacity-40" style={{ borderColor: 'var(--surf-line)' }}><ChevronLeft className="w-4 h-4" /></button>
                <span className="font-mono">{page} / {data.total_pages}</span>
                <button type="button" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} aria-label="Next page" className="w-8 h-8 flex items-center justify-center rounded-lg border disabled:opacity-40" style={{ borderColor: 'var(--surf-line)' }}><ChevronRight className="w-4 h-4" /></button>
              </span>
            )}
          </div>
        </section>

        <aside aria-label="Case preview" className="rounded-[14px] border flex flex-col xl:sticky xl:top-6 min-w-0" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          {!selectedId || preview === false ? (
            <div className="min-h-[300px] flex flex-col items-center justify-center gap-2 p-8 text-center">
              <span className="text-[15px] font-semibold">{preview === false ? 'Couldn’t load that case' : 'Select a case'}</span>
              <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>Its evidence, owner and latest activity show here.</span>
            </div>
          ) : !preview ? (
            <div className="min-h-[300px] flex items-center justify-center text-[13px]" style={{ color: 'var(--ink-faint)' }}>Loading…</div>
          ) : (
            <>
              <div className="flex flex-col gap-2.5 px-[22px] pt-[18px] pb-3.5 border-b" style={{ borderColor: 'var(--surf-line)' }}>
                <span className="flex items-center justify-between gap-3">
                  <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-link)' }}>{caseRef(preview.id)} · {preview.created_by === SYSTEM ? 'AUTO-CREATED' : `OPENED BY ${(nameOf(preview.created_by) || '').toUpperCase()}`}</span>
                  <button type="button" onClick={() => select(null)} aria-label="Close preview" className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}><X className="w-[13px] h-[13px]" strokeWidth={2} /></button>
                </span>
                <h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em] leading-tight break-words">{preview.title}</h2>
                <span className="flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>
                  <PriorityChip p={preview.priority} />{STATUS_LABEL[preview.status]} · {preview.assigned_to ? nameOf(preview.assigned_to) : 'Unassigned'} · open {age(preview.created_at)}
                </span>
              </div>
              <div className="flex flex-col gap-4 px-[22px] py-4">
                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>HOW FAR IT GOT</span>
                  <div className="grid grid-cols-7 gap-[3px]">
                    {KC_SHORT.map((s, i) => {
                      const last = furthestStage(preview.kill_chain);
                      const n = preview.kill_chain?.[i] || 0;
                      return (
                        <span key={s} className="flex flex-col gap-[5px] min-w-0">
                          <span className="h-1.5 rounded-sm" style={{ background: n ? (i === last ? 'var(--sev-crit-fg)' : 'var(--sev-high-fg)') : 'var(--surf-line)' }} />
                          <span className="text-[10px] truncate" style={{ color: n ? 'var(--ink-text)' : 'var(--ink-faint)', fontWeight: i === last ? 600 : 400 }}>{s}</span>
                        </span>
                      );
                    })}
                  </div>
                  <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-faint)' }}>{kcSentence(preview.kill_chain)}</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>EVIDENCE · {evidence.length}</span>
                  {!evidence.length && <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Nothing linked yet.</span>}
                  {evidence.slice(0, 4).map(e => (
                    <Link key={e.key} to={e.to} className="flex items-center gap-2.5 px-2.5 py-2 rounded-[9px] border min-w-0" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}>
                      <span className="inline-flex items-center gap-[5px] h-5 shrink-0 px-1.5 rounded-[5px] font-mono text-[9.5px] font-medium tracking-[0.06em]" style={{ background: `var(--sev-${sevTok(e.sev)}-bg)` }}><span className="w-[5px] h-[5px] rounded-[1px]" style={{ background: `var(--sev-${sevTok(e.sev)}-fg)` }} />{e.kind}</span>
                      <span className="min-w-0 flex flex-col gap-0.5"><span className="text-[12.5px] font-medium truncate">{e.t}</span><span className="font-mono text-[10.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{e.m}</span></span>
                    </Link>
                  ))}
                  {evidence.length > 4 && <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>and {evidence.length - 4} more on the case page</span>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>LATEST</span>
                  {(preview.timeline || []).slice(-3).reverse().map(e => (
                    <span key={`${e.kind}-${e.id}`} className="grid grid-cols-[64px_minmax(0,1fr)] gap-2 text-[12.5px] leading-normal">
                      <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{stamp(e.created_at).replace('today ', '')}</span>
                      <span className="break-words" style={{ color: 'var(--ink-muted)' }}>{e.kind === 'note' ? `${nameOf(e.actor)}: ${String(e.detail).slice(0, 120)}` : e.detail}</span>
                    </span>
                  ))}
                </div>
                {preview.status === 'closed' && preview.resolution_summary && (
                  <div className="flex flex-col gap-1 px-3 py-2.5 rounded-[9px]" style={{ background: 'var(--surf-raised)' }}>
                    <span className="font-mono text-[10px] tracking-[0.12em]">CLOSED{preview.resolution_outcome ? ` · ${OUTCOME_LABEL[preview.resolution_outcome].toUpperCase()}` : ''}</span>
                    <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{preview.resolution_summary}</span>
                  </div>
                )}
              </div>
              <div className="mt-auto flex flex-wrap items-center gap-2 px-[22px] py-3.5 border-t" style={{ borderColor: 'var(--surf-line)' }}>
                <Link to={`/cases/${preview.id}`} className={primaryBtn} style={primaryBtnStyle}>Open case</Link>
                {!preview.assigned_to && preview.status !== 'closed' && me && <button type="button" onClick={() => takeIt(preview.id)} className={secondaryBtn} style={secondaryBtnStyle}>Take it</button>}
                {NEXT[preview.status] && preview.assigned_to && <span className="text-xs ml-auto" style={{ color: 'var(--ink-faint)' }}>Next: {NEXT[preview.status][1].toLowerCase()}</span>}
              </div>
            </>
          )}
        </aside>
      </div>

      <NewCaseDrawer open={creating} members={members} me={me} onClose={() => { setCreating(false); load(); }} />
    </div>
  );
}
